var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

import React, { useCallback, useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import MaskedInput from 'react-text-mask';
import { StyledInput, StyledContainer, StyledLoading, StyledIcon, StyledBottomText } from './Input.styled';
import Spin from '../Spin';
import IconWrapper from '../IconWrapper';
import InputLabel from '../InputLabel';
import { DATA_ATTRS } from '../../utils/gtm';
export const inputLabelDataTestId = 'input-label';
export const inputIconDataTestId = 'input-icon';
export const inputHelpTextDataTestId = 'input-help-text';
export const inputMaskDataTestId = 'input-mask';
const defaultProps = {
  size: 'medium',
  type: 'text',
  placeholder: ' ',
  status: 'default',
  autoFocus: false,
  isShrink: false,
  isFocused: false,
  autoCompleteNativeStyles: false,
  placeholderColor: 'majorGray',
  color: 'majorBlack'
};
const Input = forwardRef((_a, ref) => {
  var {
    size = defaultProps.size,
    placeholder = defaultProps.placeholder,
    value,
    type = defaultProps.type,
    isLoading,
    isDisabled,
    icon,
    onChange,
    onKeyDown,
    onKeyUp,
    onKeyPress,
    onMouseDown,
    onMouseUp,
    onFocus,
    onBlur,
    onClick,
    status = defaultProps.status,
    helpText,
    mask,
    isMaskGuideEnabled,
    maskPlaceholderChar,
    pipe,
    showMask,
    keepCharPositions,
    autoFocus = defaultProps.autoFocus,
    onIconClick,
    name,
    onFilled,
    onEmpty,
    isFloatingLabel,
    dataTest,
    label,
    labelSize,
    autoComplete,
    isCenteredPlaceholder,
    isShrink = defaultProps.isShrink,
    isFocused: isPrFocused,
    autoCompleteNativeStyles = defaultProps.autoCompleteNativeStyles,
    placeholderColor = defaultProps.placeholderColor,
    color = defaultProps.color,
    hideBorderSide,
    ellipsis,
    isYMDisableKeys,
    inputMode,
    inputId
  } = _a,
      props = __rest(_a, ["size", "placeholder", "value", "type", "isLoading", "isDisabled", "icon", "onChange", "onKeyDown", "onKeyUp", "onKeyPress", "onMouseDown", "onMouseUp", "onFocus", "onBlur", "onClick", "status", "helpText", "mask", "isMaskGuideEnabled", "maskPlaceholderChar", "pipe", "showMask", "keepCharPositions", "autoFocus", "onIconClick", "name", "onFilled", "onEmpty", "isFloatingLabel", "dataTest", "label", "labelSize", "autoComplete", "isCenteredPlaceholder", "isShrink", "isFocused", "autoCompleteNativeStyles", "placeholderColor", "color", "hideBorderSide", "ellipsis", "isYMDisableKeys", "inputMode", "inputId"]);

  const isPropsFocused = isPrFocused || defaultProps.isFocused;
  const inputRef = useRef(null);
  useImperativeHandle(ref, () => {
    return {
      inputRef: inputRef.current,
      focusInput: () => {
        var _a;

        return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      },
      blurInput: () => {
        var _a;

        return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
      }
    };
  });
  useEffect(() => {
    if (!!value) {
      onFilled && onFilled();
    } else {
      onEmpty && onEmpty();
    }
  }, [value, onFilled, onEmpty]);
  const [isFocused, setIsFocused] = useState(isPropsFocused);
  useEffect(() => {
    setIsFocused(isPropsFocused);

    if (inputRef.current) {
      isPropsFocused ? inputRef.current.focus() : inputRef.current.blur();
    }
  }, [isPropsFocused]);
  const onChangeCallback = useCallback(event => {
    onChange && onChange(event, event.target.value);

    if (value === undefined) {
      if (!!event.target.value) {
        onFilled && onFilled();
      } else {
        onEmpty && onEmpty();
      }
    }
  }, [onChange, value, onFilled, onEmpty]);
  const onKeyDownCallback = useCallback(event => {
    onKeyDown && onKeyDown(event);
  }, [onKeyDown]);
  const onKeyUpCallback = useCallback(event => {
    onKeyUp && onKeyUp(event);
  }, [onKeyUp]);
  const onMouseDownCallback = useCallback(event => {
    onMouseDown && onMouseDown(event);
  }, [onMouseDown]);
  const onMouseUpCallback = useCallback(event => {
    onMouseUp && onMouseUp(event);
  }, [onMouseUp]);
  const onFocusCallback = useCallback(event => {
    setTimeout(() => setIsFocused(true));
    onFocus && onFocus(event);
  }, [onFocus]);
  const onBlurCallback = useCallback(event => {
    setTimeout(() => setIsFocused(false));
    onBlur && onBlur(event);
  }, [onBlur]);
  const onIconCallback = useCallback(event => {
    onIconClick && onIconClick(event);
  }, [onIconClick]);
  return React.createElement("div", null, React.createElement(StyledContainer, {
    isDisabled: isDisabled,
    size: size,
    isShrink: isShrink,
    isFloatingLabel: isFloatingLabel,
    isCenteredPlaceholder: isCenteredPlaceholder,
    placeholderColor: placeholderColor,
    ellipsis: ellipsis,
    isWithIcon: isLoading || Boolean(icon)
  }, mask ? React.createElement(MaskedInput, Object.assign({
    "data-testid": inputMaskDataTestId,
    name: name,
    mask: mask,
    showMask: showMask,
    pipe: pipe,
    guide: isMaskGuideEnabled,
    keepCharPositions: keepCharPositions,
    placeholderChar: maskPlaceholderChar,
    onChange: onChangeCallback,
    onKeyDown: onKeyDownCallback,
    onKeyUp: onKeyUpCallback,
    onMouseDown: onMouseDownCallback,
    onMouseUp: onMouseUpCallback,
    onFocus: onFocusCallback,
    onBlur: onBlurCallback,
    type: type,
    placeholder: placeholder,
    value: value,
    isFocused: isFocused,
    status: status,
    autoFocus: autoFocus,
    "data-test": dataTest,
    autoComplete: autoComplete,
    hideBorderSide: hideBorderSide,
    render: (ref, props) => React.createElement(StyledInput, Object.assign({
      ref: ref
    }, props, {
      id: inputId
    })),
    "data-role": "input",
    disabled: isDisabled,
    autoCompleteNativeStyles: autoCompleteNativeStyles,
    placeholderColor: placeholderColor,
    color: color,
    inputMode: inputMode,
    className: isYMDisableKeys ? 'ym-disable-keys' : ''
  }, props[DATA_ATTRS])) : React.createElement(StyledInput, Object.assign({
    ref: inputRef,
    name: name,
    onClick: onClick,
    onChange: onChangeCallback,
    onKeyDown: onKeyDownCallback,
    onKeyUp: onKeyUpCallback,
    onMouseDown: onMouseDownCallback,
    onMouseUp: onMouseUpCallback,
    onFocus: onFocusCallback,
    onBlur: onBlurCallback,
    type: type,
    placeholder: placeholder,
    value: value,
    isFocused: isFocused,
    status: status,
    autoFocus: autoFocus,
    "data-test": dataTest,
    autoComplete: autoComplete,
    "data-role": "input",
    isShrink: isShrink,
    disabled: isDisabled,
    autoCompleteNativeStyles: autoCompleteNativeStyles,
    placeholderColor: placeholderColor,
    color: color,
    hideBorderSide: hideBorderSide,
    inputMode: inputMode,
    className: isYMDisableKeys ? 'ym-disable-keys' : '',
    id: inputId
  }, props[DATA_ATTRS])), isFloatingLabel && label && React.createElement(InputLabel, {
    "data-testid": inputLabelDataTestId,
    isFloating: true,
    isRightIcon: !!icon,
    isShrink: isFocused || !!value || isShrink,
    isDisabled: isDisabled,
    size: labelSize,
    htmlFor: inputId
  }, label), !isLoading && icon && React.createElement(StyledIcon, {
    isError: status === 'error'
  }, React.createElement(IconWrapper, {
    "data-testid": inputIconDataTestId,
    onClick: onIconCallback
  }, icon)), isLoading && React.createElement(StyledLoading, null, React.createElement(Spin, {
    color: "blue",
    size: "small"
  }))), helpText && React.createElement(StyledBottomText, {
    "data-testid": inputHelpTextDataTestId,
    status: status
  }, helpText));
});
export default Input;